import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Commercials } from "@/pages/deals/types";
import { cn } from "@/utils/utils";
import IconContainer from "@/components/ui/icon-container";
import {
  Bullet,
  ChevronDown,
  Contracts,
  SquareArrowUpRight
} from "@dreamhub/designsystem/icons";
import { Button } from "@/components/ui/button";
import ContractTerms from "@/pages/deals/components/commercials/ContractTerms/ContractTerms";
import Financials from "@/pages/deals/components/commercials/Financials";
import Text from "@/components/ui/text";
import Badge from "@/components/ui/badge";
import { companyStatusColors } from "@/pages/companies/Companies";
import { CompanyStatus } from "@/pages/companies/types";
import useAccount from "@/entities/accounts/hooks";
import { simpleDate, toLocalDate } from "@/utils/datetime";
import {
  convertCurrency,
  getProductNameById,
  monetaryValueFormatter
} from "@/pages/deals/helpers";
import {
  TabOption,
  TAB_SEARCH_PARAM,
  getTabSlug
} from "@/components/layouts/DetailedView";
import { useFinancials } from "@/services/hooks/useFinancials";
import { useDealsNavigation } from "@/services/hooks/entities/deal/useDealsNavigation";
import { Product } from "@/pages/settings/components/settingsItems/hooks/types";
import { useArrMrr } from "@/services/context/ArrMrrContext";
import { IconButton } from "@/components/ui/icon-button";

const TRANSLATION_PREFIX = "companies.commercials.commercial";

type FinancialMetricType = "arr" | "mrr" | "tcv";

interface Props {
  commercial: Commercials;
  products?: Product[];
}

const CompanyCommercial: React.FC<Props> = ({ commercial, products }) => {
  const { t } = useTranslation();
  const { fieldName: arrOrMrrField, getValue: getArrOrMrrValue } = useArrMrr();

  const [isExpanded, setIsExpanded] = useState(false);
  const accountData = useAccount().data;
  const accountName = accountData ? accountData.name : t("unknown");
  const productName = getProductNameById(commercial?.productId, products);

  const {
    accountCurrencySymbol,
    contractCurrencySymbol,
    rate,
    showConversion
  } = useFinancials(commercial);

  const { navigateToDetailedView } = useDealsNavigation();

  const FinancialMetric = ({
    type,
    value
  }: {
    type: FinancialMetricType;
    value?: number | null;
  }): JSX.Element | null => {
    if (!value) return null;

    const formattedValue =
      showConversion && rate
        ? `${accountCurrencySymbol}${convertCurrency(rate, value)}`
        : monetaryValueFormatter(value, contractCurrencySymbol);

    return (
      <div className="flex gap-[6px]">
        <Text variant="body">{formattedValue}</Text>
        <Text variant="bodySecondary">
          {`(${t(`deals.commercials.${type}`)})`}
        </Text>
      </div>
    );
  };

  const ArrOrMrr = (): JSX.Element | null => {
    const type = arrOrMrrField as FinancialMetricType;
    const value = getArrOrMrrValue(commercial);
    return <FinancialMetric type={type} value={value} />;
  };

  return (
    <div
      className="flex flex-col rounded-xl cursor-pointer border border-border-widget-card bg-surface-widget-card"
      onClick={() => setIsExpanded(!isExpanded)}
    >
      <div
        className={cn(
          "flex p-5 justify-between items-center w-full",
          isExpanded ? "border-b border-border-border1" : "border-none"
        )}
      >
        <div className="flex gap-4 items-center">
          <IconButton
            variant="iconNoBg"
            className="mt-1"
            iconClassName={cn(
              "transition-transform duration-200",
              !isExpanded && "-rotate-90"
            )}
          >
            <ChevronDown />
          </IconButton>
          <div className="flex flex-col">
            <div className="flex gap-2 items-center">
              <IconContainer size="5">
                <Contracts />
              </IconContainer>
              <div className="flex items-center gap-4">
                <Text variant="lgSubtitleH1">
                  {productName
                    ? productName
                    : t(`${TRANSLATION_PREFIX}.product`, { accountName })}
                </Text>
                <Badge
                  text={
                    commercial?.isActive
                      ? t(`${TRANSLATION_PREFIX}.active`)
                      : t(`${TRANSLATION_PREFIX}.inactive`)
                  }
                  icon={
                    <IconContainer
                      style={{
                        color:
                          companyStatusColors[
                            commercial?.isActive
                              ? CompanyStatus.ACTIVE_CUSTOMER
                              : CompanyStatus.DISQUALIFIED
                          ]
                      }}
                    >
                      <Bullet />
                    </IconContainer>
                  }
                />
              </div>
            </div>
            <div className="flex gap-6">
              {commercial?.isActive && commercial?.contractEndDate && (
                <Text variant="body">
                  {t(`${TRANSLATION_PREFIX}.expiresOn`, {
                    date: simpleDate(toLocalDate(commercial?.contractEndDate))
                  })}
                </Text>
              )}
              {!commercial?.isActive &&
                commercial?.contractStartDate &&
                commercial?.contractEndDate && (
                  <Text variant="body">
                    {`${simpleDate(toLocalDate(commercial?.contractStartDate))} - ${simpleDate(toLocalDate(commercial?.contractEndDate))}`}
                  </Text>
                )}
              <ArrOrMrr />
              <FinancialMetric
                type="tcv"
                value={commercial?.totalContractValue}
              />
            </div>
          </div>
        </div>
        {commercial?.isActive && (
          <Button
            variant="link"
            className="group flex h-fit"
            onClick={(e) => {
              e.stopPropagation();
              if (commercial?.id) {
                navigateToDetailedView(commercial?.id, {
                  [TAB_SEARCH_PARAM]: getTabSlug(TabOption.COMMERCIALS)
                });
              }
            }}
          >
            <IconContainer
              size="5"
              color="link"
              className="group-hover:text-link-hover transition-colors"
            >
              <SquareArrowUpRight />
            </IconContainer>
            {t(`${TRANSLATION_PREFIX}.editInDeal`)}
          </Button>
        )}
      </div>
      <div
        className={cn(
          "grid transition-[grid-template-rows] duration-200 ease-in-out",
          isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        )}
      >
        <div className="overflow-hidden pl-11 flex">
          <div className="p-6 flex-1 flex flex-col gap-6">
            <Text variant="label">
              {t("deals.commercials.contractTermsTitle")}
            </Text>
            <ContractTerms data={commercial} isReadOnly />
          </div>
          <div className="w-px bg-border-border1 m-4"></div>
          <div className="p-6 flex-1 flex flex-col gap-6">
            <Text variant="label">
              {t("deals.commercials.financialsTitle")}
            </Text>
            <Financials data={commercial} isReadOnly />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyCommercial;
